import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const ParticipantsOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(20px);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const ParticipantsPanel = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
`;

const Header = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
`;

const Title = styled.h2`
  color: white;
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ParticipantCount = styled.span`
  background: rgba(255, 107, 53, 0.2);
  color: ${props => props.theme.colors.primary};
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const CloseButton = styled(motion.button)`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const SearchSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: white;
  padding: 0.75rem;
  font-size: 1rem;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.2);
  }
`;

const ParticipantsContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 107, 53, 0.5);
    border-radius: 3px;
  }
`;

const ParticipantItem = styled(motion.div)`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const Avatar = styled(motion.div)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.isLocal ? 
    'linear-gradient(135deg, #10b981, #059669)' :
    'linear-gradient(135deg, #FF6B35, #F7931E)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  font-weight: 600;
  position: relative;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const StatusIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${props => {
    switch (props.status) {
      case 'speaking': return '#10b981';
      case 'muted': return '#ef4444';
      case 'away': return '#f59e0b';
      default: return '#6b7280';
    }
  }};
  border: 2px solid rgba(0, 0, 0, 0.8);
`;

const ParticipantInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ParticipantName = styled.div`
  color: white;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${props => props.isLocal && `
    color: #10b981;
  `}
`;

const ParticipantRole = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ParticipantStatus = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ActionButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RoleIcon = styled.span`
  font-size: 0.8rem;
`;

const ConnectionQuality = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
`;

const QualityBar = styled.div`
  display: flex;
  gap: 1px;
`;

const QualityLevel = styled.div`
  width: 3px;
  height: 8px;
  background: ${props => props.active ? 
    (props.level === 'poor' ? '#ef4444' : 
     props.level === 'good' ? '#f59e0b' : '#10b981') :
    'rgba(255, 255, 255, 0.2)'
  };
  border-radius: 1px;
`;

const EmptyState = styled(motion.div)`
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
`;

const InviteSection = styled(motion.div)`
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
`;

const InviteButton = styled(motion.button)`
  width: 100%;
  background: linear-gradient(135deg, #10b981, #059669);
  border: none;
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
  }
`;

function ParticipantsList({ participants, onClose, currentUserId = 'local' }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  const filteredParticipants = participants.filter(participant =>
    participant.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getParticipantStatus = (participant) => {
    if (!participant.connected) return 'offline';
    if (participant.isSpeaking) return 'speaking';
    if (!participant.audioEnabled) return 'muted';
    if (participant.isAway) return 'away';
    return 'online';
  };

  const getConnectionQuality = (participant) => {
    if (!participant.connected) return 'poor';
    if (participant.latency < 50) return 'excellent';
    if (participant.latency < 150) return 'good';
    return 'poor';
  };

  const getParticipantInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleMuteParticipant = (participantId) => {
    // Implementation would depend on WebRTC setup
    console.log('Mute participant:', participantId);
  };

  const handleRemoveParticipant = (participantId) => {
    // Implementation would depend on room permissions
    console.log('Remove participant:', participantId);
  };

  const handleInviteMore = () => {
    const roomUrl = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: 'Unisciti alla mia sala LuminAI Connect',
        url: roomUrl
      });
    } else {
      navigator.clipboard.writeText(roomUrl);
      // Show toast notification
    }
  };

  const renderConnectionQuality = (quality) => {
    const levels = ['poor', 'good', 'excellent'];
    const currentLevel = levels.indexOf(quality);

    return (
      <ConnectionQuality>
        <QualityBar>
          {levels.map((level, index) => (
            <QualityLevel
              key={level}
              active={index <= currentLevel}
              level={quality}
            />
          ))}
        </QualityBar>
      </ConnectionQuality>
    );
  };

  return (
    <ParticipantsOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ParticipantsPanel
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <div>
            <Title>
              👥 Partecipanti
              <ParticipantCount>{participants.length}</ParticipantCount>
            </Title>
          </div>
          <CloseButton
            onClick={onClose}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            ✕
          </CloseButton>
        </Header>

        <SearchSection>
          <SearchInput
            type="text"
            placeholder="Cerca partecipanti..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchSection>

        <ParticipantsContainer>
          <AnimatePresence>
            {filteredParticipants.length > 0 ? (
              filteredParticipants.map((participant, index) => {
                const isLocal = participant.id === currentUserId;
                const status = getParticipantStatus(participant);
                const quality = getConnectionQuality(participant);

                return (
                  <ParticipantItem
                    key={participant.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Avatar
                      isLocal={isLocal}
                      whileHover={{ scale: 1.05 }}
                    >
                      {getParticipantInitials(participant.displayName)}
                      <StatusIndicator
                        status={status}
                        animate={{
                          scale: status === 'speaking' ? [1, 1.2, 1] : 1
                        }}
                        transition={{
                          duration: 0.5,
                          repeat: status === 'speaking' ? Infinity : 0
                        }}
                      />
                    </Avatar>

                    <ParticipantInfo>
                      <ParticipantName isLocal={isLocal}>
                        {participant.displayName}
                        {isLocal && ' (Tu)'}
                        {participant.role === 'host' && (
                          <RoleIcon>👑</RoleIcon>
                        )}
                      </ParticipantName>

                      <ParticipantRole>
                        <span>
                          {participant.role === 'host' ? 'Host' : 'Partecipante'}
                        </span>
                        {participant.connected && renderConnectionQuality(quality)}
                      </ParticipantRole>

                      <ParticipantStatus>
                        {status === 'speaking' && '🗣️ Sta parlando'}
                        {status === 'muted' && '🔇 Microfono spento'}
                        {status === 'away' && '⏰ Assente'}
                        {status === 'offline' && '📵 Disconnesso'}
                        {status === 'online' && !participant.audioEnabled && '🔕 Audio spento'}
                        {status === 'online' && participant.audioEnabled && '🔊 Online'}
                      </ParticipantStatus>
                    </ParticipantInfo>

                    {!isLocal && (
                      <ActionButtons>
                        <ActionButton
                          onClick={() => handleMuteParticipant(participant.id)}
                          disabled={participant.role === 'host'}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          title="Silenzia"
                        >
                          🔇
                        </ActionButton>
                        
                        <ActionButton
                          onClick={() => handleRemoveParticipant(participant.id)}
                          disabled={participant.role === 'host'}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          title="Rimuovi"
                        >
                          🚫
                        </ActionButton>
                      </ActionButtons>
                    )}
                  </ParticipantItem>
                );
              })
            ) : (
              <EmptyState
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔍</div>
                <div>Nessun partecipante trovato</div>
              </EmptyState>
            )}
          </AnimatePresence>
        </ParticipantsContainer>

        <InviteSection>
          <InviteButton
            onClick={handleInviteMore}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span>📤</span>
            Invita Altri
          </InviteButton>
        </InviteSection>
      </ParticipantsPanel>
    </ParticipantsOverlay>
  );
}

export default ParticipantsList;
